import React, { useEffect, useRef, useState } from 'react';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import { Icon } from '@/components/ui/Icon';
import { useT } from '@/i18n/labels';

/** One selectable option on an enumeration property. Value and label routinely differ. */
interface ConvPropOption {
  value: string;
  label: string;
}

/** One of the tenant's ticket properties, as the panel renders it. */
interface ConvProp {
  name: string;
  label: string;
  type: string;
  fieldType?: string | null;
  options: ConvPropOption[];
  readOnly: boolean;
  editable: boolean;
  value?: string | null;
}

/** The CRM ticket-priority property: shown by the priority picker instead, so it is not listed twice. */
const isPriorityProperty = (name: string): boolean => (name ?? '').trim().toLowerCase() === 'hs_ticket_priority';

interface ConvPropsState {
  available: boolean;
  hasTicket: boolean;
  readFailed: boolean;
  properties: ConvProp[];
  reason?: string | null;
}

/**
 * The tenant's own CRM ticket properties on this conversation — the Conversation Properties card.
 *
 * <b>Which properties appear is the tenant's decision, made in the CRM mapping screen</b>, and so is which
 * of them an agent may change. This component renders whatever the server says and has no vocabulary of its
 * own; there is deliberately no hard-coded field anywhere in it.
 *
 * <b>Values are the CRM's, read live.</b> Unlike the category picker next to it — which shows what we last
 * wrote and therefore cannot see a change made in the portal — a value here is what the ticket holds. That is
 * also why a failed read says so rather than rendering as "empty": across a whole card, silently showing
 * nothing would misreport the tenant's own data back at them.
 *
 * Shipped COLLAPSED. The panel is about the conversation first (OP-623), and a tenant with a dozen mapped
 * properties would otherwise push the conversation's own details off the top.
 */
export const ConversationProperties: React.FC<{
  sessionId: string;
  /** Someone else owns this conversation (console v2): values are shown, not editable. */
  disabled?: boolean;
}> = ({ sessionId, disabled = false }) => {
  const { t } = useT();

  const [state, setState] = useState<ConvPropsState | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingName, setSavingName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const savingRef = useRef(false);
  const revision = useRef(0);

  const [open, setOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem('ac-ctx-props-open') === '1';
    } catch {
      return false;
    }
  });

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('ac-ctx-props-open', next ? '1' : '0');
      } catch {
        /* a private-mode browser must not break the panel */
      }
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    let sequence = 0;
    const editing = () => {
      const active = document.activeElement;
      return !!active && !!cardRef.current?.contains(active) &&
        (active instanceof HTMLInputElement || active instanceof HTMLSelectElement || active instanceof HTMLTextAreaElement);
    };
    const refresh = (initial = false) => {
      if (!initial && (savingRef.current || editing())) return;
      const request = ++sequence;
      const startedAt = revision.current;
      void axiosClient.get<ConvPropsState>(API.CRM.SESSION_PROPERTIES(sessionId))
        .then((res) => {
          if (!cancelled && request === sequence && startedAt === revision.current && !savingRef.current &&
              (initial || !editing())) setState(res.data);
        })
        .catch(() => { if (!cancelled && initial) setState(null); })
        .finally(() => { if (!cancelled && initial) setLoading(false); });
    };
    const onFocus = () => refresh();
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    refresh(true);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible') refresh(); }, 30000);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [sessionId]);

  const save = async (prop: ConvProp, next: string) => {
    if (next === (prop.value ?? '')) return;

    savingRef.current = true;
    revision.current++;
    setSavingName(prop.name);
    setError(null);

    // Optimistic, with the previous value kept so a rejection can restore it.
    const previous = prop.value ?? null;
    setState((s) =>
      s ? { ...s, properties: s.properties.map((p) => (p.name === prop.name ? { ...p, value: next } : p)) } : s,
    );

    try {
      const res = await axiosClient.put<ConvPropsState>(API.CRM.SESSION_PROPERTIES(sessionId), {
        name: prop.name,
        value: next,
      });
      // The server re-reads the ticket, so this reflects what the portal actually stored — it may have
      // normalised a date or trimmed a string, and showing our submitted value back would claim otherwise.
      setState(res.data);
    } catch {
      setState((s) =>
        s
          ? {
              ...s,
              properties: s.properties.map((p) => (p.name === prop.name ? { ...p, value: previous } : p)),
            }
          : s,
      );
      setError(t('ctx_conv_props_save_failed'));
    } finally {
      savingRef.current = false;
      setSavingName(null);
    }
  };

  if (loading) return null;

  // Nothing configured. Silent on purpose: an agent cannot act on it, and a permanent "not configured"
  // notice on every conversation is noise. The mapping screen is where it gets fixed.
  if (!state || !state.available) return null;

  const renderControl = (prop: ConvProp) => {
    const busy = savingName === prop.name;
    const current = prop.value ?? '';

    if (!prop.editable) {
      const display = prop.type === 'enumeration'
        ? current.split(';').filter(Boolean).map(value => prop.options.find(option => option.value === value)?.label ?? value).join(', ')
        : current;
      return display
        ? <span className="ac-ctx-kv-v">{display}</span>
        : <span className="ac-ctx-muted">—</span>;
    }

    const common = {
      disabled: busy || disabled,
      'data-testid': `ctx-conv-prop-input-${prop.name}`,
      style: {
        fontSize: 12,
        fontFamily: 'inherit',
        color: 'var(--text)',
        background: 'transparent',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '2px 6px',
        maxWidth: 150,
        opacity: busy || disabled ? 0.6 : 1,
      } as React.CSSProperties,
    };

    if (prop.type === 'enumeration') {
      return (
        <select
          {...common}
          value={current}
          onChange={(e) => void save(prop, e.target.value)}
          aria-label={prop.label}
        >
          {/* Always offered: a property an agent can set is one they can unset. */}
          <option value="">{t('ctx_conv_props_unset')}</option>
          {prop.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }

    if (prop.type === 'bool') {
      return (
        <input
          {...common}
          type="checkbox"
          checked={current === 'true'}
          onChange={(e) => void save(prop, e.target.checked ? 'true' : 'false')}
          aria-label={prop.label}
          style={{ ...common.style, maxWidth: 'unset' }}
        />
      );
    }

    // Text, number and date all commit on blur or Enter rather than per keystroke — each save is a live CRM
    // write, and a per-keystroke one would be a write per character.
    const inputType = prop.type === 'number' ? 'number' : prop.type === 'datetime' ? 'date' : 'text';

    return (
      <input
        key={`${prop.name}:${current}`}
        {...common}
        type={inputType}
        defaultValue={current}
        onBlur={(e) => void save(prop, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') (e.target as HTMLInputElement).value = current;
        }}
        aria-label={prop.label}
      />
    );
  };

  return (
    <div ref={cardRef} className="ac-ctx-sec" data-testid="ctx-conv-props">
      <button
        type="button"
        className="ac-ctx-sec-toggle"
        onClick={toggle}
        aria-expanded={open}
        data-testid="ctx-conv-props-toggle"
      >
        <span className="ac-ctx-sec-title">{t('ctx_conv_props_section')}</span>
        <Icon name="chevron" size={14} />
      </button>

      {open && (
        <>
          {/*
            Three different emptinesses, deliberately worded apart. "No ticket yet" is normal early in a
            conversation's life; "the read failed" means the values are UNKNOWN, not absent; and a ticket that
            vanished from the portal is neither. Rendering one blank card for all three would send an agent
            looking in the wrong place.
          */}
          {state.readFailed ? (
            <div className="ac-ctx-na" data-testid="ctx-conv-props-read-failed">
              {state.reason || t('ctx_conv_props_read_failed')}
            </div>
          ) : !state.hasTicket ? (
            <div className="ac-ctx-na" data-testid="ctx-conv-props-no-ticket">
              {t('ctx_conv_props_no_ticket')}
            </div>
          ) : (
            // The conversation's priority has its own control beside the status (ConversationPriorityPicker): the CRM's
            // ticket-priority property is hidden here so priority appears once.
            state.properties.filter((prop) => !isPriorityProperty(prop.name)).map((prop) => (
              <div key={prop.name} className="ac-ctx-kv" data-testid={`ctx-conv-prop-${prop.name}`}>
                <span className="ac-ctx-kv-k">{prop.label}</span>
                <span className="ac-ctx-kv-v">{renderControl(prop)}</span>
              </div>
            ))
          )}

          {error && (
            <div
              className="ac-ctx-na"
              data-testid="ctx-conv-props-error"
              style={{ color: 'var(--danger, #c0392b)', marginTop: 6 }}
            >
              {error}
            </div>
          )}
        </>
      )}
    </div>
  );
};
